import * as React from "react";

import { getClosestNode, getScrollParent, checkClosestClassSelector, getElementNodeIndex } from "./utils";

export type SlipperOptions = {
  handleSelector: string;
  onChange: (initialIndex: number, finalIndex: number) => void;
};

type TargetNode = {
  node: HTMLElement;
  center: number;
  offset: number;
  scrollParent: Element | null;
  originalScrollHeight: number;
  initialScrollTop: number;
  height: number;
  startPageY: number;
  initialIndex?: number;
};

export const useSlipper = ({ handleSelector, onChange }: SlipperOptions) => {
  const targetNode = React.useRef<TargetNode>();
  const otherNodesData = React.useRef<any>([]);
  const isAnimating = React.useRef(false);
  const container = React.useRef<HTMLElement | null>(null);

  const transformWithoutTransition = (element: HTMLElement, transformCSSValue: string) => {
    element.classList.add("no-transition");
    element.style.transform = transformCSSValue;
    setTimeout(() => {
      element.classList.remove("no-transition");
    });
  };

  const performMovement = React.useRef<boolean>(false);
  const setTargetNodeData = (
    node: HTMLElement,
    startPageY: number,
    {
      initialScrollTop,
      initialIndex,
    }: {
      initialScrollTop?: number;
      initialIndex?: number;
    } = {}
  ) => {
    const scrollParent = getScrollParent(node);
    targetNode.current = {
      node,
      center: node.offsetTop + node.offsetHeight / 2,
      offset: 0,
      scrollParent,
      originalScrollHeight: scrollParent ? scrollParent.scrollHeight : 0,
      initialScrollTop: initialScrollTop !== undefined ? initialScrollTop : scrollParent ? scrollParent.scrollTop : 0,
      height: node.offsetHeight,
      startPageY,
      initialIndex: initialIndex !== undefined ? initialIndex : getElementNodeIndex(node),
    };
  };

  const setOtherNodesData = () => {
    const targetNodeRef = targetNode.current!.node;
    otherNodesData.current = [];
    container.current?.childNodes.forEach((child: ChildNode) => {
      const { offsetTop, offsetHeight } = child as HTMLElement;
      if (child !== targetNodeRef) {
        otherNodesData.current.push({
          node: child,
          center: offsetTop,
          height: offsetHeight,
          offset: offsetTop + offsetHeight / 2 - targetNode.current!.center,
        });
      }
    });
  };

  const handleTouchStart = React.useCallback((e: TouchEvent) => {
    e.stopPropagation();

    const target = getClosestNode(e.target as HTMLElement, "li");
    const isValidTarget = target && target.nodeType === Node.ELEMENT_NODE;
    if (isValidTarget) {
      const shouldPerformMovement = handleSelector
        ? checkClosestClassSelector(handleSelector, e.target as HTMLElement, target)
        : true;
      performMovement.current = isValidTarget && shouldPerformMovement;
      if (performMovement.current) {
        e.preventDefault();
        setTargetNodeData(target, e.touches[0].pageY);
        setOtherNodesData();
      }
    }
  }, []);

  const updateScrolling = (currentPageY: number) => {
    if (container.current) {
      const { top, height } = container.current.getBoundingClientRect();
      const scrollContainer = targetNode.current!.scrollParent;
      if (
        top + height - currentPageY < 50 &&
        targetNode.current!.originalScrollHeight - scrollContainer!.scrollTop > scrollContainer!.clientHeight
      ) {
        scrollContainer!.scrollTop += 3;
      } else if (currentPageY - top < 40 && scrollContainer!.scrollTop > 0) {
        scrollContainer!.scrollTop -= 3;
      }
    }
  };

  const handleTouchMove = React.useCallback((e: TouchEvent) => {
    e.stopPropagation();
    if (targetNode.current) {
      const { scrollParent, initialScrollTop, startPageY, node, height, initialIndex } = targetNode.current!;

      if (performMovement.current) {
        e.preventDefault();
        const currentPageY = e.touches[0].pageY;
        updateScrolling(currentPageY);
        const elaspedScrollMovement = scrollParent!.scrollTop - initialScrollTop;
        const delta = currentPageY - startPageY + elaspedScrollMovement;
        node.style.transform = ` rotate(-1deg) translateY(${delta}px)`;

        node.style.zIndex = `1`;
        otherNodesData.current.forEach((otherNodeData: any) => {
          const nodeLowerBound = otherNodeData.offset;
          const upCondition =
            nodeLowerBound < 0 &&
            delta < 0 &&
            delta < nodeLowerBound &&
            delta > nodeLowerBound - otherNodeData.height / 2;
          const downCondition =
            nodeLowerBound > 0 &&
            delta > 0 &&
            delta > nodeLowerBound &&
            delta < nodeLowerBound + otherNodeData.height / 2;

          if (upCondition || downCondition) {
            if (isAnimating.current === true) return;
            isAnimating.current = true;

            const tHandler = () => {
              transformWithoutTransition(node, "");
              transformWithoutTransition(otherNodeData.node, "");
              if (upCondition) {
                const newInsertedTargetNode = (node.parentNode as Node).insertBefore(node, otherNodeData.node);
                setTargetNodeData(newInsertedTargetNode, currentPageY, { initialIndex });
              }
              if (downCondition) {
                (node.parentNode as Node).insertBefore(otherNodeData.node, node);
                setTargetNodeData(node, currentPageY, { initialIndex });
              }
              setOtherNodesData();
              otherNodeData.node.removeEventListener("transitionend", tHandler);
              isAnimating.current = false;
            };
            otherNodeData.node.addEventListener("transitionend", tHandler);
            const deltaMovement = upCondition ? height + 10 : -height - 10;
            otherNodeData.node.style.transform = `translateY(${deltaMovement}px)`;
          }
        });
      }
    }
  }, []);

  const handleTouchEnd = React.useCallback(
    (e: TouchEvent) => {
      e.stopPropagation();
      if (performMovement.current) {
        const { node, initialIndex = -1 } = targetNode.current!;
        e.preventDefault();
        node.style.transform = `rotate(0deg)`;
        node.style.zIndex = `0`;
        const finalIndex = getElementNodeIndex(node);
        if (finalIndex !== initialIndex) {
          onChange(initialIndex, finalIndex);
        }
      }
    },
    [onChange]
  );

  const setRef = React.useCallback(
    (node) => {
      if (container.current) {
        // Make sure to cleanup any events/references added to the last instance
        container.current.removeEventListener("touchstart", handleTouchStart, false);
        container.current.removeEventListener("touchmove", handleTouchMove, false);
        container.current.removeEventListener("touchend", handleTouchEnd, false);
      }

      if (node) {
        // Check if a node is actually passed. Otherwise node would be null.
        // You can now do what you need to, addEventListeners, etc.
        node.addEventListener("touchstart", handleTouchStart, false);
        node.addEventListener("touchmove", handleTouchMove, false);
        node.addEventListener("touchend", handleTouchEnd, false);
      }
      container.current = node;
    },
    [handleTouchStart, handleTouchMove, handleTouchEnd]
  );

  return setRef;
};
